import { AuthController, DPOAuth } from '@dialpad/auth';
import type { App, Plugin } from 'vue';
import { inject } from 'vue';
import type { hostEnv } from './constants';
import logger from '@/logger';

const log = logger.getChild('auth-controller');
const AUTH_CONTROLLER_INJECTION_KEY = Symbol('powerdialer-auth-controller');
const DP_OAUTH_INJECTION_KEY = Symbol('powerdialer-dp-oauth');

export type Auth = {
  authController: AuthController;
  dpOAuth: DPOAuth;
  maybeExchangeAuthCode: () => Promise<boolean>;
  redirectToLogin: (redirectUrl: string) => void;
};

const SESSION_STORAGE_AUTH_KEY = 'redirectStates';

export const hostAuthController = (context: {
  env: typeof hostEnv;
  vueApp: App;
}): { auth: Auth } => {
  const { OAUTH_URL, DP_BUILD_OAUTH_CLIENT_ID, DP_BUILD_OAUTH_SCOPES } =
    context.env;
  const dpOAuth = new DPOAuth({
    dpBaseUri: OAUTH_URL,
    clientId: DP_BUILD_OAUTH_CLIENT_ID,
    redirectUri: '/',
    scopes: DP_BUILD_OAUTH_SCOPES,
  });
  const authController = new AuthController();

  return {
    auth: {
      authController,
      dpOAuth,
      maybeExchangeAuthCode: async (): Promise<boolean> => {
        const fragment = globalThis.location.hash.slice(1);
        const params = new URLSearchParams(fragment);

        const code = params.get('code');
        const state = params.get('state');

        if (code && state) {
          try {
            // retrieve previously persisted values
            const storedState = JSON.parse(
              sessionStorage.getItem(SESSION_STORAGE_AUTH_KEY) ?? '{}',
            );
            const authData = await dpOAuth.getToken(
              { code, state },
              storedState,
            );

            authController.setAuthData(authData);
            return true;
          } catch {
            return false;
          }
        }

        return false;
      },
      redirectToLogin: (redirectUrl: string): void => {
        const { authorizeUri, stateToStore } = dpOAuth.createAuthorizeUri({
          redirectUri: redirectUrl,
        });
        sessionStorage.setItem(
          SESSION_STORAGE_AUTH_KEY,
          JSON.stringify(stateToStore),
        );
        location.href = authorizeUri;
      },
    },
  };
};

const authControllerPlugin: Plugin<{
  authController: AuthController;
  dpOAuth: DPOAuth;
}> = {
  install(app, { authController, dpOAuth }) {
    log.log('installing authController plugin');
    app.provide(AUTH_CONTROLLER_INJECTION_KEY, authController);
    app.provide(DP_OAUTH_INJECTION_KEY, dpOAuth);
  },
};

export const provideAuthController = async ({
  auth,
  vueApp,
}: {
  auth: Auth;
  vueApp: App;
}): Promise<void> => {
  log.log('providing AuthController');

  vueApp.use(authControllerPlugin, {
    authController: auth.authController,
    dpOAuth: auth.dpOAuth,
  });
};

export const useAuthController = (): AuthController => {
  const authController = inject<AuthController>(AUTH_CONTROLLER_INJECTION_KEY);

  if (!authController) {
    throw new Error('useAuthController must be used within a vue context');
  }

  return authController;
};

export const useDPOAuth = (): DPOAuth => {
  const dpOAuth = inject<DPOAuth>(DP_OAUTH_INJECTION_KEY);

  if (!dpOAuth) {
    throw new Error('useDPOAuth must be used within a vue context');
  }

  return dpOAuth;
};
